import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axiosInstance from '../../helpers/axios';
import PaginationTable from '../status/PaginationTable';

const EXPORT_TYPES = [
    { value: 'applications', label: 'Applications' },
    { value: 'loans', label: 'Loans' },
    { value: 'collections', label: 'Collections' },
];

// Real statuses from this app's application pipeline (src/App.js status routes).
// Loan/Collection status enums aren't confirmed yet, so the status filter only
// applies to Applications for now.
// Keeps long option lists from overflowing the viewport and getting shifted above the select.
const SELECT_MENU_PROPS = {
    variant: 'menu',
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    PaperProps: { style: { maxHeight: 250 } },
};

const APPLICATION_STATUSES = [
    'Submission', 'Processing', 'Review', 'Pending', 'Sanction',
    'Disbursement', 'Disbursed', 'Rejected', 'Dropped', 'OnHold',
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ROWS_PER_PAGE_OPTIONS = [15, 50, 100, 500];

function statusChipColor(status) {
    if (status === 'Delivered') return 'success';
    if (status === 'Processing') return 'warning';
    if (status === 'Failed') return 'error';
    return 'default';
}

function formatDateTime(value) {
    if (!value) return '—';
    return new Date(value).toLocaleString('en-IN', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

function AuditLogSkeleton() {
    return (
        <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                    {Array.from({ length: 5 }).map((_, j) => (
                        <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                </TableRow>
            ))}
        </TableBody>
    );
}

export default function ExportAuditTab() {
    const [exportType, setExportType] = useState('applications');
    const [recipientEmail, setRecipientEmail] = useState('');
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [isTriggering, setIsTriggering] = useState(false);
    const [triggerError, setTriggerError] = useState(null);

    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [isLoadingLogs, setIsLoadingLogs] = useState(true);
    const [logsError, setLogsError] = useState(null);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        setStatusFilter('');
    }, [exportType]);

    const loadAuditLog = useCallback(() => {
        setIsLoadingLogs(true);
        setLogsError(null);
        axiosInstance
            .get('/admin-settings/export/audit-log', { params: { page: currentPage, limit: rowsPerPage } })
            .then((response) => {
                setLogs(response.data.data.logs);
                setTotal(response.data.data.total);
            })
            .catch(() => setLogsError('Could not load export history.'))
            .finally(() => setIsLoadingLogs(false));
    }, [currentPage, rowsPerPage]);

    const handleRowsPerPageChange = (size) => {
        setRowsPerPage(size);
        setCurrentPage(1);
    };

    useEffect(() => {
        loadAuditLog();
    }, [loadAuditLog]);

    const canTrigger = EMAIL_REGEX.test(recipientEmail.trim()) && !isTriggering;

    const handleClearFilters = () => {
        setExportType('applications');
        setRecipientEmail('');
        setFromDate(null);
        setToDate(null);
        setStatusFilter('');
    };

    const handleTriggerExport = () => {
        setIsTriggering(true);
        setTriggerError(null);
        const filters = {
            fromDate: fromDate ? fromDate.format('YYYY-MM-DD') : undefined,
            toDate: toDate ? toDate.format('YYYY-MM-DD') : undefined,
            status: statusFilter || undefined,
        };
        axiosInstance
            .post('/admin-settings/export/trigger', { type: exportType, filters, recipientEmail: recipientEmail.trim() })
            .then(() => {
                setSnackbar({
                    open: true,
                    message: `Export queued. Email will be sent to ${recipientEmail} within 5 minutes.`,
                    severity: 'success',
                });
                loadAuditLog();
            })
            .catch((error) => {
                setTriggerError(error?.response?.data?.message || 'Could not queue export.');
            })
            .finally(() => setIsTriggering(false));
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                <Card variant="outlined" sx={{ marginBottom: '24px' }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ marginBottom: '8px' }}>Export Data</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '20px' }}>
                            Exports are delivered via email for security. Select the data type and filters, then provide recipient email.
                        </Typography>

                        {triggerError && <Alert severity="error" sx={{ marginBottom: '16px' }}>{triggerError}</Alert>}

                        <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            <Select
                                size="small"
                                value={exportType}
                                onChange={(e) => setExportType(e.target.value)}
                                sx={{ minWidth: 180 }}
                                MenuProps={SELECT_MENU_PROPS}
                            >
                                {EXPORT_TYPES.map((t) => (
                                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                ))}
                            </Select>

                            <TextField
                                size="small"
                                label="Recipient Email"
                                required
                                value={recipientEmail}
                                onChange={(e) => setRecipientEmail(e.target.value)}
                                error={recipientEmail.length > 0 && !EMAIL_REGEX.test(recipientEmail.trim())}
                                sx={{ minWidth: 240 }}
                            />

                            <DatePicker
                                label="From Date"
                                value={fromDate}
                                onChange={setFromDate}
                                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                            />
                            <DatePicker
                                label="To Date"
                                value={toDate}
                                onChange={setToDate}
                                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                            />

                            {exportType === 'applications' && (
                                <Select
                                    size="small"
                                    displayEmpty
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    sx={{ minWidth: 160 }}
                                    MenuProps={SELECT_MENU_PROPS}
                                >
                                    <MenuItem value="">All Statuses</MenuItem>
                                    {APPLICATION_STATUSES.map((s) => (
                                        <MenuItem key={s} value={s}>{s}</MenuItem>
                                    ))}
                                </Select>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                            <Button
                                variant="contained"
                                onClick={handleTriggerExport}
                                disabled={!canTrigger}
                                sx={{ color: '#fff' }}
                            >
                                {isTriggering ? 'Sending...' : 'Send Export'}
                            </Button>
                            <button
                                onClick={handleClearFilters}
                                aria-label="Clear filters"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    height: '35px',
                                    padding: '0 16px',
                                    backgroundColor: '#D22129',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: 'white',
                                    fontFamily: 'Inter-Medium',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    alignSelf: 'center',
                                }}
                            >
                                <RefreshIcon />
                            </button>
                        </Box>
                    </CardContent>
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <Typography variant="h6">Export History</Typography>
                    <IconButton size="small" onClick={loadAuditLog}>
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </Box>

                {logsError && <Alert severity="error" sx={{ marginBottom: '12px' }}>{logsError}</Alert>}

                <Paper variant="outlined" sx={{ borderRadius: '8px' }}>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ backgroundColor: '#F8F8F8' }}>
                                <TableRow>
                                    <TableCell>Export Type</TableCell>
                                    <TableCell>Triggered By</TableCell>
                                    <TableCell>Date & Time</TableCell>
                                    <TableCell>Recipient Email</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            {isLoadingLogs ? (
                                <AuditLogSkeleton />
                            ) : logs.length === 0 ? (
                                <TableBody>
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', padding: '32px' }}>
                                            No exports triggered yet.
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            ) : (
                                <TableBody>
                                    {logs.map((log) => (
                                        <TableRow key={log.id} hover>
                                            <TableCell><Chip size="small" label={log.type} /></TableCell>
                                            <TableCell>{log.triggeredBy}</TableCell>
                                            <TableCell>{formatDateTime(log.triggeredAt)}</TableCell>
                                            <TableCell>{log.recipientEmail}</TableCell>
                                            <TableCell>
                                                <Chip size="small" color={statusChipColor(log.status)} label={log.status} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>
                </Paper>

                {total > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '16px', marginBottom: '10px', flexWrap: 'wrap', gap: '4px' }}>
                        <Box sx={{ fontWeight: 'bold', fontFamily: 'Inter-Medium', fontSize: '14px', marginRight: '10px' }}>
                            Showing {(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, total)} records of {total} |
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ fontWeight: 600, marginRight: '8px' }}>Rows Per Pages :</Typography>
                            {ROWS_PER_PAGE_OPTIONS.map((size) => (
                                <Box
                                    key={size}
                                    component="span"
                                    onClick={() => handleRowsPerPageChange(size)}
                                    sx={{
                                        cursor: 'pointer',
                                        padding: '10px',
                                        borderRadius: '8px',
                                        color: rowsPerPage === size ? '#D32028' : 'black',
                                        backgroundColor: rowsPerPage === size ? '#FFF2F3' : 'transparent',
                                    }}
                                >
                                    {size}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                <PaginationTable
                    startPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={total}
                    paginate={setCurrentPage}
                />

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </LocalizationProvider>
    );
}
